import React, { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "@/lib/utils";
import { Checkbox } from "./checkbox";
import EllipsisTooltip from "@component/tooltip/EllipsisTooltip";
import { Menu } from "lucide-react";
import { Button } from "./button";
import CustomTooltip from "./CustomTooltip";

const ColumnsFilterPopup = ({
  disabled: disable = false,
  containerRef,
  disabledPortal = true,
  contentClassName,
  columnVisibility,
  data = [],
  handleApply = () => null,
  iconButton = true,
  buttonName = "Customize Columns",
}) => {
  const [columns, setColumns] = useState([]);
  const [open, setOpen] = useState(false); //
  useEffect(() => {
    if (!data) return;

    let cols = data.map((v) => ({
      columnName: v?.id,
      checked: columnVisibility?.[v?.id] ?? true,
      displayName: v?.columnDef.header,
      disabled:
        typeof v?.columnDef?.enableHiding === "boolean"
          ? v?.columnDef?.enableHiding
          : true,
    }));

    const index = cols.findIndex((v) => v.columnName === "mrt-row-actions");
    let rowActions = null;

    if (index !== -1 && cols.length - 1 !== index) {
      rowActions = cols.splice(index, 1)[0];
    }

    if (rowActions) {
      cols.push(rowActions);
    }

    setColumns(cols);
  }, [JSON.stringify(data)]);

  const handleClose = (boo = false) => {
    if (!boo) {
      const changeData = columns?.map((v) => {
        return { ...v, checked: columnVisibility?.[v?.columnName] ?? true };
      });
      setColumns(changeData);
    }
    setOpen(boo);
  };

  const handleChecked = (item) => {
    const changeData = columns?.map((v) => {
      if (v?.columnName === item?.columnName) {
        return { ...v, checked: !v?.checked };
      }
      return v;
    });
    setColumns(changeData);
  };

  const handleApplyData = (col) => {
    const obj = {};
    col.forEach((v) => {
      obj[v?.columnName] = v?.checked;
    });
    handleApply(obj);
    setOpen(false);
  };

  const disabled = disable || columns?.length === 0;
  return (
    <Popover open={open} onOpenChange={(boo) => handleClose(boo)}>
      <PopoverTrigger
        disabled={disabled}
        asChild={!disabled}
        className="w-full h-full"
      >
        {iconButton ? (
        <div>
          <CustomTooltip content="show/hide columns" delayDuration={0}>
            <Button
              variant="outline"
              size="sm"
              disabled={disabled}
              className="flex items-center justify-center min-h-10 min-w-[42px] border border-card-border"
              onClick={() => setOpen(true)}
            >
              <Menu />
            </Button>
          </CustomTooltip>
        </div> ) : (
          <Button variant="outline" className="mt-3 md:mt-0 border-primary text-primary hover:bg-primary-foreground">{buttonName}</Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        disabledPortal={disabledPortal}
        sideOffset={4}
        containerRef={containerRef} // attach to container}
        className={cn("bg-primary-foreground me-4 pb-0 ", contentClassName)}
      >
        <div className="overflow-y-auto max-h-[calc(var(--radix-popper-available-height)-80px)]">
          {columns?.length ? (
            columns.map((item, i) => (
              <div key={i}>
                <div
                  className={cn(
                    "items-center gap-2 cursor-pointer m-1 flex select-none",
                    !item?.disabled &&
                      "opacity-50 cursor-not-allowed pointer-events-none"
                  )}
                  onClick={() => handleChecked(item, i)}
                >
                  <Checkbox
                    checked={item?.checked}
                    className={cn("mt-1", item?.checked && "border-primary")}
                    onChange={() => handleChecked(item, i)}
                  />
                  <EllipsisTooltip containerRef={containerRef}>
                    <span className="text-sm">{item?.displayName}</span>
                  </EllipsisTooltip>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-sm py-5 pb-8">No data found</div>
          )}
        </div>
        {columns.length ? (
          <div className="flex gap-2 pb-3">
            <Button
              className="w-full"
              variant="outline"
              onClick={() => handleClose(false)}
            >
              Close
            </Button>
            <Button className="w-full" onClick={() => handleApplyData(columns)}>
              Apply
            </Button>
          </div>
        ) : (
          ""
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ColumnsFilterPopup;
